/**
 * RegieTestAdminPage — tableau de bord Kayla (protégé par WorkOS, cf.
 * `RegieTestAdminGate` dans `App.tsx`). Pleine largeur, 3 onglets :
 * Leads / Couverture des shifts / Liens candidats. La table des leads est le
 * MÊME composant que celui monté dans Organisation → Acquisition — une seule
 * table, deux endroits d'où la regarder. Pas de
 * `CockpitShell` — cette page EST son propre chrome.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { Button, EnTeteDePanneau, Onglets } from "@/shared/ui";
import { Settings } from "@/shared/ui/icons";

import { adminApi, countActiveItems } from "./api";
import { AdminKpis } from "./admin/AdminKpis";
import { CandidateFiche } from "./admin/CandidateFiche";
import { ConfigEditorModal } from "./admin/ConfigEditorModal";
import { CoverageTab } from "./admin/CoverageTab";
import { LinksTab } from "./admin/LinksTab";
import { LeadsTable } from "./admin/LeadsTable";

type Tab = "passations" | "coverage" | "links";

const TABS: { id: Tab; label: string }[] = [
  { id: "passations", label: "Leads" },
  { id: "coverage", label: "Couverture des shifts" },
  { id: "links", label: "Liens candidats" },
];

export function RegieTestAdminPage() {
  const [tab, setTab] = useState<Tab>("passations");
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [configOpen, setConfigOpen] = useState(false);
  const queryClient = useQueryClient();

  const candidatesQuery = useQuery({
    queryKey: ["regie-test-admin-candidates"],
    queryFn: () => adminApi.listCandidates(),
  });
  const coverageQuery = useQuery({
    queryKey: ["regie-test-admin-coverage"],
    queryFn: () => adminApi.getCoverage(),
  });
  const itemsQuery = useQuery({
    queryKey: ["regie-test-admin-items"],
    queryFn: () => adminApi.listItems(),
  });

  const candidates = candidatesQuery.data ?? [];
  const coverage = coverageQuery.data ?? null;
  const activeItemsCount = itemsQuery.data ? countActiveItems(itemsQuery.data) : null;

  function refetchCandidates() {
    void queryClient.invalidateQueries({ queryKey: ["regie-test-admin-candidates"] });
  }

  return (
    <div className="min-h-dvh bg-canvas px-4 py-6 sm:px-8 sm:py-10">
      <div className="mx-auto flex max-w-[1280px] flex-col gap-5">
        <EnTeteDePanneau
          titre="Test de qualification — tableau de bord"
          sousTitre={
            <>
              Qui décide bien, et qui peut couvrir mes créneaux.
              {/* La ligne tient sa place avant que le compte n'arrive : l'en-tête ne
                  change pas de hauteur au chargement, et les onglets ne sautent pas. */}
              <span className="mt-1 block min-h-[1.5em] text-[12px] text-faint">
                {activeItemsCount !== null ? `${activeItemsCount} cartes actives dans la banque` : " "}
              </span>
            </>
          }
          actions={
            <>
              <Onglets
                style="segments"
                nom="Sections"
                options={TABS.map((t) => ({ valeur: t.id, etiquette: t.label }))}
                valeur={tab}
                onChange={setTab}
              />
              <Button
                type="button"
                variant="secondary"
                size="lg"
                icon={<Settings className="h-4 w-4" />}
                onClick={() => setConfigOpen(true)}
              >
                Réglages barème
              </Button>
            </>
          }
        />

        <AdminKpis candidates={candidates} coverage={coverage} />

        {tab === "passations" && (
          <LeadsTable
            leads={candidates}
            loading={candidatesQuery.isLoading}
            onSelect={setSelectedCandidateId}
          />
        )}
        {tab === "coverage" && <CoverageTab matrix={coverage} loading={coverageQuery.isLoading} />}
        {tab === "links" && <LinksTab candidates={candidates} onCreated={refetchCandidates} />}

        <p className="max-w-[1280px] text-[13px] leading-relaxed text-faint">
          <b className="text-muted">Rappel des garde-fous :</b> le score n'est
          jamais montré au candidat · le tirage des cartes est unique par
          candidat (jamais deux fois le même test) · le scoring est 100&nbsp;%
          serveur · le temps par carte est un simple signal pour ta lecture,
          jamais une sanction.
        </p>
      </div>

      <CandidateFiche
        candidateId={selectedCandidateId}
        onClose={() => setSelectedCandidateId(null)}
      />
      <ConfigEditorModal isOpen={configOpen} onClose={() => setConfigOpen(false)} />
    </div>
  );
}
